/**
 * Moteur Comptable Autonome — Bascule vers SAGE Comptabilité
 * Agence Africa For Tourism (AFT) - TravelManager v2.0
 * 
 * Règles Métier :
 * - Chiffre d'Affaires (C.A.) = Marge uniquement (Ventes brutes - Débours/Achats)
 * - Tourisme : Crédit 706xxx (Marge) & Crédit 471xxx (Débours)
 * - Billetterie : Crédit 701xxx (Marge) & Crédit 471xxx (Achats billets)
 * - Client : Débit 411100 / Compte auxiliaire tiers pour le TTC
 * - TVA : Crédit 443200 pour la TVA 18% (si soumise TVA1)
 */

import dayjs from 'dayjs';

// ─── PARAMÈTRES PAR DÉFAUT VALIDÉS SELON LE PLAN COMPTABLE SAGE AFT ─────────

export const DEFAULT_COMPTES_GENERAUX = [
    { id: '1', numero: '411100', intitule: 'Collectif clients', nature: 'Client', sensNormal: 'Débit', codeTva: '' },
    { id: '2', numero: '411300', intitule: 'CLIENT VENTES AU COMPTANT', nature: 'Client', sensNormal: 'Débit', codeTva: '' },
    { id: '3', numero: '411900', intitule: 'CLIENT FACTURATION', nature: 'Client', sensNormal: 'Crédit', codeTva: '' },
    { id: '4', numero: '706110', intitule: 'VENTES CIRCUITS', nature: 'Produits', sensNormal: 'Crédit', codeTva: 'TVA1' },
    { id: '5', numero: '706120', intitule: 'VENTES EXCURSIONS', nature: 'Produits', sensNormal: 'Crédit', codeTva: 'TVA1' },
    { id: '6', numero: '701100', intitule: 'VENTES BILLETS D\'AVION', nature: 'Produits', sensNormal: 'Crédit', codeTva: 'TVA0' },
    { id: '7', numero: '471100', intitule: 'DEBOURS SEJOURS', nature: 'Aucune', sensNormal: 'Crédit', codeTva: '' },
    { id: '8', numero: '471120', intitule: 'DEBOURS CIRCUITS/EXCURSIONS', nature: 'Aucune', sensNormal: 'Crédit', codeTva: '' },
    { id: '9', numero: '471130', intitule: 'DEBOURS TRANSFERTS/AUTRES', nature: 'Aucune', sensNormal: 'Crédit', codeTva: '' },
    { id: '10', numero: '471110', intitule: 'DEBOURS BILLETS D\'AVION', nature: 'Aucune', sensNormal: 'Crédit', codeTva: '' },
    { id: '11', numero: '443200', intitule: 'ETAT - TVA FACTUREE', nature: 'Aucune', sensNormal: 'Crédit', codeTva: '' },
    { id: '12', numero: '521000', intitule: 'Banque', nature: 'Trésorerie', sensNormal: 'Débit', codeTva: '' },
    { id: '13', numero: '571000', intitule: 'Caisse', nature: 'Trésorerie', sensNormal: 'Débit', codeTva: '' }
];

export const DEFAULT_JOURNAUX = [
    { id: '1', code: 'FAC', intitule: 'Journal de facturation', type: 'Facturation', numerotation: 'Continue', description: 'Facturation clients (Tourisme et Billetterie) — un journal unique pour l’ensemble de la facturation' },
    { id: '2', code: 'VTES', intitule: 'Journal des ventes', type: 'Ventes', numerotation: 'Continue', description: 'Opérations de ventes — Reconnaissance du C.A. (marges), Débours et TVA' }
];

export const DEFAULT_CODES_TVA = [
    { id: '1', code: 'TVA1', intitule: 'TVA taux normal 18%', sens: 'Facturée', taux: 18, compteTaxe: '443200', description: 'Prestations touristiques soumises' },
    { id: '2', code: 'TVA0', intitule: 'Exonéré — 0%', sens: 'Facturée', taux: 0, compteTaxe: '', description: 'Billetterie aérienne (hors taxes)' }
];

export const INITIAL_CONFIRMED_TIERS_MAP = {
    'aims': '4111AIMS',
    'aspt': '4111ASPT',
    'clients divers': '4111DIVERS',
    'client comptant': '4111DIVERS',
    'pierre fabre': '4111FABRE',
    'sonaged': '4111SONAG',
    'ordre des avocats': '4111AVOC',
    'elecnor': '4111ELEC'
};

export const DEFAULT_SCHEMAS = [
    {
        id: 'FAC_STANDARD',
        codeJournal: 'FAC',
        intitule: 'Facturation client unique',
        typeOperation: 'Facturation',
        lignes: [
            { no: 1, compteGeneral: '411100', typeTiers: 'Client (tiers)', libelleAuto: 'FACT. [N°facture] - [Nom client]', sens: 'Débit', montantType: 'TTC' },
            { no: 2, compteGeneral: '4711xx', typeTiers: '', libelleAuto: 'Débours : achats/dossiers', sens: 'Crédit', montantType: 'Debours' },
            { no: 3, compteGeneral: '7061xx / 701100', typeTiers: '', libelleAuto: 'FACT. [N°facture] - [Nom client]', sens: 'Crédit', montantType: 'Marge' },
            { no: 4, compteGeneral: '443200', typeTiers: '', libelleAuto: 'TVA FACT. [N°facture]', sens: 'Crédit', montantType: 'TVA' }
        ]
    }
];

export const DEFAULT_EXPORT_SETTINGS = {
    formatFichier: 'TEXT_DELIMITED', // ou 'SAGE_FIXED'
    separateur: ';',
    extension: 'txt',
    prefixeFichier: 'TM_BASCULE_',
    dossierDestination: 'C:\\Users\\HP\\Desktop',
    codeDossierSage: 'AFRICA FOR TOURISM',
    encodage: 'UTF-8'
};

// ─── GESTION DU STOCKAGE PERSISTANT DES PARAMÈTRES ───────────────────────────

export const getSageSettings = () => {
    try {
        const savedComptes = localStorage.getItem('aft_sage_comptes');
        const savedJournaux = localStorage.getItem('aft_sage_journaux');
        const savedCodesTva = localStorage.getItem('aft_sage_codes_tva');
        const savedExport = localStorage.getItem('aft_sage_export_settings');

        return {
            comptes: savedComptes ? JSON.parse(savedComptes) : DEFAULT_COMPTES_GENERAUX,
            journaux: savedJournaux ? JSON.parse(savedJournaux) : DEFAULT_JOURNAUX,
            codesTva: savedCodesTva ? JSON.parse(savedCodesTva) : DEFAULT_CODES_TVA,
            exportSettings: savedExport ? JSON.parse(savedExport) : DEFAULT_EXPORT_SETTINGS
        };
    } catch {
        return {
            comptes: DEFAULT_COMPTES_GENERAUX,
            journaux: DEFAULT_JOURNAUX,
            codesTva: DEFAULT_CODES_TVA,
            exportSettings: DEFAULT_EXPORT_SETTINGS
        };
    }
};

export const saveSageSettings = ({ comptes, journaux, codesTva, exportSettings }) => {
    if (comptes) localStorage.setItem('aft_sage_comptes', JSON.stringify(comptes));
    if (journaux) localStorage.setItem('aft_sage_journaux', JSON.stringify(journaux));
    if (codesTva) localStorage.setItem('aft_sage_codes_tva', JSON.stringify(codesTva));
    if (exportSettings) localStorage.setItem('aft_sage_export_settings', JSON.stringify(exportSettings));
};

// ─── GESTION DE L'HISTORIQUE DES TRANSFERTS ET FACTURES TRANSFÉRÉES ──────────

export const getTransferHistory = () => {
    try {
        const saved = localStorage.getItem('aft_sage_transfer_history');
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
};

export const saveTransferRecord = (transferRecord) => {
    const history = getTransferHistory();
    const updated = [transferRecord, ...history];
    localStorage.setItem('aft_sage_transfer_history', JSON.stringify(updated));

    // Mémoriser les IDs des factures transférées
    const transferredFactureIds = getTransferredFactureIds();
    transferRecord.factures.forEach(f => {
        transferredFactureIds[f.id] = {
            refTransfert: transferRecord.numeroTransfert,
            dateTransfert: transferRecord.dateValidation
        };
    });
    localStorage.setItem('aft_sage_transferred_invoices', JSON.stringify(transferredFactureIds));
    return updated;
};

export const getTransferredFactureIds = () => {
    try {
        const saved = localStorage.getItem('aft_sage_transferred_invoices');
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
};

export const cancelTransferRecord = (numeroTransfert, user = 'Administrateur', motif = 'Annulation exceptionnelle') => {
    const history = getTransferHistory();
    const targetTransfer = history.find(t => t.numeroTransfert === numeroTransfert);
    if (!targetTransfer) return false;

    // Retirer les factures de la liste des factures transférées
    const transferredFactureIds = getTransferredFactureIds();
    targetTransfer.factures.forEach(f => {
        delete transferredFactureIds[f.id];
    });
    localStorage.setItem('aft_sage_transferred_invoices', JSON.stringify(transferredFactureIds));

    // Mettre à jour le statut dans l'historique
    const updatedHistory = history.map(t => {
        if (t.numeroTransfert === numeroTransfert) {
            return {
                ...t,
                statut: 'ANNULÉ',
                dateAnnulation: new Date().toISOString(),
                annulePar: user,
                motifAnnulation: motif
            };
        }
        return t;
    });
    localStorage.setItem('aft_sage_transfer_history', JSON.stringify(updatedHistory));

    // Enregistrement dans le journal des événements (Règle 6)
    try {
        const events = JSON.parse(localStorage.getItem('aft_journal_evenements') || '[]');
        events.unshift({
            id: 'EVT_' + Date.now(),
            date: new Date().toISOString(),
            type: 'ANNULATION_BASCULE_SAGE',
            utilisateur: user,
            description: `Annulation du transfert ${numeroTransfert} (${targetTransfer.factures.length} factures). Motif : ${motif}`
        });
        localStorage.setItem('aft_journal_evenements', JSON.stringify(events));
    } catch (e) {
        console.error('Erreur journal événements:', e);
    }

    return true;
};

// ─── EXTRACTION DU COMPTE TIERS CLIENT SAGE ──────────────────────────

export const getClientSageAuxCodeMap = () => {
    try {
        const custom = JSON.parse(localStorage.getItem('aft_sage_client_tiers_map') || '{}');
        return { ...INITIAL_CONFIRMED_TIERS_MAP, ...custom };
    } catch {
        return { ...INITIAL_CONFIRMED_TIERS_MAP };
    }
};

export const saveClientSageAuxCodeMap = (map) => {
    localStorage.setItem('aft_sage_client_tiers_map', JSON.stringify(map));
};

export const generateCleanNominalSageCode = (rawName = '') => {
    if (!rawName) return '4111Àcréer';
    const trimmed = rawName.trim();
    const lower = trimmed.toLowerCase();

    // 1. Vérification dans le référentiel des comptes officiels confirmés AFT
    if (INITIAL_CONFIRMED_TIERS_MAP[lower]) {
        return INITIAL_CONFIRMED_TIERS_MAP[lower];
    }
    for (const [key, code] of Object.entries(INITIAL_CONFIRMED_TIERS_MAP)) {
        if (lower === key || lower.includes(key) || key.includes(lower)) {
            return code;
        }
    }

    // 2. Gestion des ventes comptant / cash / divers
    if (lower.includes('comptant') || lower.includes('cash') || lower.includes('divers')) {
        return '4111DIVERS';
    }

    // 3. Nettoyage des préfixes non significatifs
    let clean = trimmed.toUpperCase();
    clean = clean.replace(/^(HOTEL|PHARMACIE|CLINIQUE|ECOLE|SARL|STE|ETS|MAISON|AGENCE)[-_\s]+/i, '');
    clean = clean.replace(/^(MR|MME|DR|ME|PROF)[-_\s]+/i, '');
    clean = clean.replace(/[^A-Z0-9]/g, '');

    if (!clean) return '4111Àcréer';

    // Format SAGE : 4111 + Nom du client (ex: 4111FABRE, 4111BROGNI, 4111NDIAYE)
    const suffix = clean.slice(0, 8);
    return `4111${suffix}`;
};

export const getClientFullName = (client, fallbackNom = '') => {
    if (!client && !fallbackNom) return 'Client inconnu';
    if (typeof client === 'string') return client.trim();
    if (client?.structure) {
        return client.nom || client.raisonSociale || client.libelle || client.nomComplet || fallbackNom || 'Client entreprise';
    }
    const prenom = (client?.prenom || '').trim();
    const nom = (client?.nom || '').trim();
    if (prenom && nom) {
        return `${prenom} ${nom}`;
    }
    return client?.nomComplet || client?.libelle || nom || prenom || client?.raisonSociale || fallbackNom || 'Client inconnu';
};

export const getClientSageAuxCode = (client, nomClient = '') => {
    const fullName = getClientFullName(client, nomClient);
    const rawNom = (client?.nom || client?.libelle || client?.nomComplet || nomClient || fullName || '').trim();
    const cleanNomKey = rawNom.toLowerCase();
    const cleanFullNameKey = fullName.toLowerCase();
    const clientId = client?.id ? String(client.id) : null;

    try {
        const savedMap = getClientSageAuxCodeMap();
        if (clientId && savedMap[clientId] && savedMap[clientId].startsWith('4111') && !savedMap[clientId].includes('Àcréer')) {
            return savedMap[clientId];
        }
        if (cleanFullNameKey && savedMap[cleanFullNameKey] && savedMap[cleanFullNameKey].startsWith('4111') && !savedMap[cleanFullNameKey].includes('Àcréer')) {
            return savedMap[cleanFullNameKey];
        }
        if (cleanNomKey && savedMap[cleanNomKey] && savedMap[cleanNomKey].startsWith('4111') && !savedMap[cleanNomKey].includes('Àcréer')) {
            return savedMap[cleanNomKey];
        }
    } catch {}

    // Déduction stricte et propre par le nom (règle 4111[NOM])
    return generateCleanNominalSageCode(client?.nom || rawNom);
};

// ─── UTILITAIRE DE PARSING SÉCURISÉ DE DATE ─────────────────────────────────

export const parseSafeDate = (d) => {
    if (!d) return dayjs();
    if (typeof d === 'string') {
        const trimmed = d.trim();
        if (trimmed.includes('/')) {
            const parts = trimmed.split('/');
            if (parts.length === 3) {
                const day = parts[0].padStart(2, '0');
                const month = parts[1].padStart(2, '0');
                let year = parts[2];
                if (year.length === 2) year = `20${year}`;
                const parsed = dayjs(`${year}-${month}-${day}`);
                if (parsed.isValid()) return parsed;
            }
        } else if (trimmed.includes('-')) {
            const parts = trimmed.split('-');
            if (parts.length === 3 && parts[0].length <= 2) {
                const day = parts[0].padStart(2, '0');
                const month = parts[1].padStart(2, '0');
                let year = parts[2];
                if (year.length === 2) year = `20${year}`;
                const parsed = dayjs(`${year}-${month}-${day}`);
                if (parsed.isValid()) return parsed;
            }
        }
    }
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed : dayjs();
};

// ─── RECHERCHE DES FACTURES ÉLIGIBLES AU TRANSFERT SAGE ─────────────────────

export const getEligibleFactures = (allFactures = [], startDateStr = null, endDateStr = null) => {
    const transferredMap = getTransferredFactureIds();
    const start = startDateStr ? dayjs(startDateStr).startOf('day') : null;
    const end = endDateStr ? dayjs(endDateStr).endOf('day') : null;

    return (allFactures || []).filter(f => {
        // Règle 1 & 3 : Exclusion absolue des factures déjà transférées
        if (transferredMap[f.id] || f.statutTransfert === 'Transférée' || f.isTransferred) {
            return false;
        }

        // Vérification de la date de facture
        const rawDate = f.date_creation || f.dateFacture || f.date || f.created_at;
        const fDate = rawDate ? dayjs(rawDate) : null;
        if (start && fDate && fDate.isBefore(start)) return false;
        if (end && fDate && fDate.isAfter(end)) return false;

        // Factures valides (non brouillon ou non annulées sauf avoirs)
        const statut = (f.statut || f.status || '').toUpperCase();
        if (statut === 'BROUILLON') return false;

        return true;
    }).map(f => {
        // Enrichissement des montants selon la règle Métier AFT
        const rawTTC = Number(f.total_ttc || f.montantTotal || f.montantTTC || f.ttc || f.total || 0);
        const rawHT = Number(f.total_ht || f.montantHT || f.ht || 0);
        const rawTVA = Number(f.total_tva || f.montantTVA || f.tva || 0);

        // Détection de la nature (Billetterie vs Tourisme/Production)
        const isBilleterie = Boolean(
            f.typePrestation === 'BILLETTERIE' ||
            f.prestation === 'BILLETTERIE' ||
            f.type === 'BILLETTERIE' ||
            f.planeTickets?.length > 0 ||
            f.pnr ||
            f.billeterie
        );

        // Calculs des débours et de la marge AFT
        let debours = 0;
        let marge = 0;
        let tva = rawTVA;
        let ttc = rawTTC;

        if (isBilleterie) {
            // Billetterie aérienne : Exonérée TVA (TVA0)
            // Débours = Achats billets, Marge = Prix Vente - Prix Achat
            const montantAchat = Number(f.montantAchat || f.totalAchat || f.debours || 0);
            if (montantAchat > 0) {
                debours = montantAchat;
                marge = Math.max(0, ttc - debours);
            } else {
                // Si non renseigné, estimation marge / débours standard ou 100% produit
                marge = rawHT > 0 ? rawHT : Math.round(ttc * 0.10);
                debours = ttc - marge;
            }
            tva = 0; // Exonéré TVA0
        } else {
            // Tourisme (Circuits, Séjours, etc.) : Soumis TVA1 (18%)
            const deboursProd = Number(f.debours || f.totalDebours || f.montantAchat || 0);
            if (deboursProd > 0) {
                debours = deboursProd;
                marge = Math.max(0, rawHT > 0 ? rawHT : (ttc - tva - debours));
            } else {
                debours = Math.max(0, ttc - tva - (rawHT > 0 ? rawHT : Math.round(ttc * 0.15)));
                marge = Math.max(0, ttc - tva - debours);
            }
        }

        const clientNom = getClientFullName(f.client, f.clientNom || f.nom_client || f.nomClient || f.client?.libelle || f.client?.nomComplet || 'Client inconnu');
        const clientCodeTiers = getClientSageAuxCode(f.client, clientNom);
        const numeroFacture = f.numero_facture || f.numeroFacture || f.code || `FAC-${f.id}`;
        const refDossier = f.numero_dossier || f.numeroDossier || f.pnr || f.dossierRef || '—';

        // Calcul automatique de la date d'échéance selon le délai du client (ex: 30j par défaut)
        const delai = Number(f.client?.delaiPaiement || 30);
        const parsedDate = parseSafeDate(f.date_creation || f.dateFacture || f.date);
        const rawDateFacture = parsedDate.format('YYYY-MM-DD');
        const dateFacture = parsedDate.format('DD/MM/YYYY');
        const dateEcheance = parsedDate.add(delai, 'day').format('DD/MM/YYYY');

        return {
            id: f.id,
            numeroFacture,
            dateFacture,
            rawDateFacture,
            clientNom,
            clientCodeTiers,
            refDossier,
            nature: isBilleterie ? 'Billetterie' : 'Production',
            journalCode: 'FAC',
            montantHT: marge > 0 ? marge : rawHT,
            debours,
            tva,
            montantTTC: ttc,
            dateEcheance,
            delaiPaiement: delai,
            isBilleterie,
            statutTransfert: 'À transférer'
        };
    });
};

// ─── GÉNÉRATION DES ÉCRITURES COMPTABLES SAGE (SIMULATION & TRANSFERT) ───────

export const generateSageEcritures = (selectedFactures = [], settings = null) => {
    const activeSettings = settings || getSageSettings();
    const ecritures = [];
    const anomalies = [];

    // Table de contrôle
    let cptTiersInexistants = 0;
    let cptFacturesSansProduit = 0;
    let cptDesequilibrees = 0;
    let cptMontantsIncoherents = 0;
    let cptDejaTransferees = 0;

    const transferredMap = getTransferredFactureIds();

    selectedFactures.forEach(f => {
        // 1. Détection facture déjà transférée
        if (transferredMap[f.id]) {
            cptDejaTransferees++;
            anomalies.push({
                type: 'DEJA_TRANSFEREE',
                facture: f.numeroFacture,
                client: f.clientNom,
                message: `La facture ${f.numeroFacture} a déjà été transférée.`
            });
        }

        // 2. Détection tiers à créer
        const isTiersACreer = !f.clientCodeTiers || f.clientCodeTiers === '4111Àcréer' || f.clientCodeTiers.includes('Àcréer');
        if (isTiersACreer) {
            cptTiersInexistants++;
            anomalies.push({
                type: 'TIERS_INEXISTANT',
                facture: f.numeroFacture,
                client: f.clientNom,
                message: `Compte tiers manquant pour "${f.clientNom}". Assigné temporairement à 4111Àcréer.`
            });
        }

        // 3. Montants nuls ou incohérents
        if (f.montantTTC <= 0) {
            cptMontantsIncoherents++;
            anomalies.push({
                type: 'MONTANT_INCOHERENT',
                facture: f.numeroFacture,
                client: f.clientNom,
                message: `Montant TTC nul ou négatif (${f.montantTTC} CFA).`
            });
        }

        const parsedFactureDate = parseSafeDate(f.rawDateFacture || f.dateFacture);
        const jourNum = parsedFactureDate.date();
        const jour = isNaN(jourNum) ? dayjs().format('DD') : String(jourNum).padStart(2, '0');
        const refCompta = f.refDossier && f.refDossier !== '—' ? f.refDossier : (f.isBilleterie ? `PNR-${f.numeroFacture}` : `DOS-${f.numeroFacture}`);

        // Définition des comptes selon le plan comptable confirmé AFT
        const isComptant = f.clientCodeTiers === '4111DIVERS' || (f.clientNom || '').toUpperCase().includes('COMPTANT') || (f.clientNom || '').toUpperCase().includes('CASH');
        const compteClient = isComptant 
            ? (activeSettings.comptes?.find(c => c.numero === '411300')?.numero || '411300')
            : (activeSettings.comptes?.find(c => c.numero === '411100')?.numero || '411100');

        let compteProduit = '706110';
        let compteDebours = '471120';

        if (f.isBilleterie) {
            compteProduit = activeSettings.comptes?.find(c => c.numero === '701100')?.numero || '701100';
            compteDebours = activeSettings.comptes?.find(c => c.numero === '471110')?.numero || '471110';
        } else {
            const natureText = `${f.nature || ''} ${f.refDossier || ''} ${f.commentaire || ''}`.toLowerCase();
            if (natureText.includes('excursion')) {
                compteProduit = activeSettings.comptes?.find(c => c.numero === '706120')?.numero || '706120';
                compteDebours = activeSettings.comptes?.find(c => c.numero === '471120')?.numero || '471120';
            } else if (natureText.includes('sejour') || natureText.includes('séjour') || natureText.includes('hotel') || natureText.includes('hébergement')) {
                compteProduit = activeSettings.comptes?.find(c => c.numero === '706110')?.numero || '706110';
                compteDebours = activeSettings.comptes?.find(c => c.numero === '471100')?.numero || '471100';
            } else if (natureText.includes('transfert')) {
                compteProduit = activeSettings.comptes?.find(c => c.numero === '706110')?.numero || '706110';
                compteDebours = activeSettings.comptes?.find(c => c.numero === '471130')?.numero || '471130';
            } else {
                compteProduit = activeSettings.comptes?.find(c => c.numero === '706110')?.numero || '706110';
                compteDebours = activeSettings.comptes?.find(c => c.numero === '471120')?.numero || '471120';
            }
        }
        const compteTvaFacturee = activeSettings.comptes?.find(c => c.numero === '443200')?.numero || '443200';

        // ── Ligne 1 : Débit Client TTC ──
        ecritures.push({
            id: `${f.id}_L1`,
            factureId: f.id,
            numeroFacture: f.numeroFacture,
            jour,
            dateFacture: f.dateFacture,
            piece: 'néant',
            numeroDossier: refCompta,
            compteGeneral: compteClient,
            compteTiers: isComptant ? '4111DIVERS' : (f.clientCodeTiers || '4111Àcréer'),
            libelle: `FACT.${f.numeroFacture} - ${f.clientNom}`,
            dateEcheance: f.dateEcheance,
            positionJournal: 'néant',
            debit: f.montantTTC,
            credit: 0,
            journal: 'FAC'
        });

        // ── Ligne 2 : Crédit Débours (Achats ou débours production) ──
        if (f.debours > 0) {
            ecritures.push({
                id: `${f.id}_L2`,
                factureId: f.id,
                numeroFacture: f.numeroFacture,
                jour,
                dateFacture: f.dateFacture,
                piece: 'néant',
                numeroDossier: refCompta,
                compteGeneral: compteDebours,
                compteTiers: '—',
                libelle: f.isBilleterie ? 'DEBOURS BILLETS D\'AVION' : 'DEBOURS CIRCUITS/EXCURSIONS',
                dateEcheance: '—',
                positionJournal: 'néant',
                debit: 0,
                credit: f.debours,
                journal: 'FAC'
            });
        }

        // ── Ligne 3 : Crédit Produit / Marge HT ──
        if (f.montantHT > 0) {
            ecritures.push({
                id: `${f.id}_L3`,
                factureId: f.id,
                numeroFacture: f.numeroFacture,
                jour,
                dateFacture: f.dateFacture,
                piece: 'néant',
                numeroDossier: refCompta,
                compteGeneral: compteProduit,
                compteTiers: '—',
                libelle: `FACT.${f.numeroFacture} - ${f.clientNom}`,
                dateEcheance: '—',
                positionJournal: 'néant',
                debit: 0,
                credit: f.montantHT,
                journal: 'FAC'
            });
        }

        // ── Ligne 4 : Crédit TVA Facturée 18% (si soumise) ──
        if (f.tva > 0) {
            ecritures.push({
                id: `${f.id}_L4`,
                factureId: f.id,
                numeroFacture: f.numeroFacture,
                jour,
                dateFacture: f.dateFacture,
                piece: 'néant',
                numeroDossier: refCompta,
                compteGeneral: compteTvaFacturee,
                compteTiers: '—',
                libelle: `TVA FACT.${f.numeroFacture}`,
                dateEcheance: '—',
                positionJournal: 'néant',
                debit: 0,
                credit: f.tva,
                journal: 'FAC'
            });
        }

        // Contrôle d'équilibre individuel de la facture
        const totalDebitFacture = f.montantTTC;
        const totalCreditFacture = f.debours + f.montantHT + f.tva;
        if (Math.abs(totalDebitFacture - totalCreditFacture) > 1) {
            cptDesequilibrees++;
            anomalies.push({
                type: 'DESEQUILIBRE',
                facture: f.numeroFacture,
                client: f.clientNom,
                message: `Écriture déséquilibrée pour ${f.numeroFacture} : Débit=${totalDebitFacture} vs Crédit=${totalCreditFacture}`
            });
        }
    });

    const totalDebitGeneral = ecritures.reduce((sum, e) => sum + Number(e.debit || 0), 0);
    const totalCreditGeneral = ecritures.reduce((sum, e) => sum + Number(e.credit || 0), 0);
    const isEquilibre = Math.abs(totalDebitGeneral - totalCreditGeneral) < 1;

    // Récapitulatif par compte mouvementé
    const comptesRecapMap = {};
    ecritures.forEach(e => {
        const key = e.compteGeneral;
        if (!comptesRecapMap[key]) {
            const matchedCompte = activeSettings.comptes?.find(c => c.numero === key);
            let intitule = matchedCompte?.intitule || 'Compte général';
            if (key === '411100') intitule = 'Collectif clients';
            else if (key === '411300') intitule = 'CLIENT VENTES AU COMPTANT';
            else if (key === '411900') intitule = 'CLIENT FACTURATION';
            else if (key === '706110') intitule = 'VENTES CIRCUITS';
            else if (key === '706120') intitule = 'VENTES EXCURSIONS';
            else if (key === '701100') intitule = 'VENTES BILLETS D\'AVION';
            else if (key === '471100') intitule = 'DEBOURS SEJOURS';
            else if (key === '471120') intitule = 'DEBOURS CIRCUITS/EXCURSIONS';
            else if (key === '471130') intitule = 'DEBOURS TRANSFERTS/AUTRES';
            else if (key === '471110') intitule = 'DEBOURS BILLETS D\'AVION';
            else if (key === '443200') intitule = 'ETAT - TVA FACTUREE';

            comptesRecapMap[key] = {
                compte: key,
                intitule,
                journal: e.journal || 'FAC',
                totalDebit: 0,
                totalCredit: 0
            };
        }
        comptesRecapMap[key].totalDebit += Number(e.debit || 0);
        comptesRecapMap[key].totalCredit += Number(e.credit || 0);
    });

    const comptesRecap = Object.values(comptesRecapMap);

    return {
        ecritures,
        totalDebit: totalDebitGeneral,
        totalCredit: totalCreditGeneral,
        isEquilibre,
        comptesRecap,
        anomalies,
        controleStats: [
            { id: '1', point: 'Comptes de tiers inexistants dans SAGE', nombre: cptTiersInexistants, resultat: cptTiersInexistants === 0 ? 'RAS' : `${cptTiersInexistants} À CRÉER` },
            { id: '2', point: 'Factures sans compte de produit paramétré', nombre: cptFacturesSansProduit, resultat: 'RAS' },
            { id: '3', point: 'Écritures déséquilibrées (débit ≠ crédit)', nombre: cptDesequilibrees, resultat: cptDesequilibrees === 0 ? 'RAS' : `${cptDesequilibrees} ANOMALIE(S)` },
            { id: '4', point: 'Montants nuls ou incohérents', nombre: cptMontantsIncoherents, resultat: cptMontantsIncoherents === 0 ? 'RAS' : `${cptMontantsIncoherents} ANOMALIE(S)` },
            { id: '5', point: 'Factures déjà transférées détectées dans la sélection', nombre: cptDejaTransferees, resultat: cptDejaTransferees === 0 ? 'RAS' : `${cptDejaTransferees} REJETÉES` }
        ]
    };
};

// ─── GÉNÉRATION DU CONTENU DU FICHIER SAGE (.TXT & .CSV) ────────────────────

export const buildSageExportFileContent = (ecritures = [], exportSettings = DEFAULT_EXPORT_SETTINGS) => {
    const sep = exportSettings.separateur || ';';
    const lines = [];

    // En-tête standard SAGE
    lines.push(['Journal', 'Date', 'N° Piece', 'Reference', 'Compte General', 'Compte Tiers', 'Libelle', 'Echeance', 'Debit', 'Credit'].join(sep));

    ecritures.forEach(e => {
        const row = [
            e.journal || 'FAC',
            e.dateFacture || '',
            e.piece === 'néant' ? '' : (e.piece || ''),
            e.numeroDossier || '',
            e.compteGeneral || '',
            e.compteTiers === '—' ? '' : (e.compteTiers || ''),
            e.libelle || '',
            e.dateEcheance === '—' ? '' : (e.dateEcheance || ''),
            Number(e.debit || 0) > 0 ? Number(e.debit).toString() : '0',
            Number(e.credit || 0) > 0 ? Number(e.credit).toString() : '0'
        ];
        lines.push(row.join(sep));
    });

    return lines.join('\r\n');
};

// ─── TÉLÉCHARGEMENT DU FICHIER SAGE (.TXT & .CSV) ───────────────────────────

export const downloadSageFile = (fileContent, filename) => {
    const isCsv = filename.toLowerCase().endsWith('.csv');
    // Pour les CSV, ajouter le préfixe UTF-8 BOM (\uFEFF) pour compatibilité automatique Excel et SAGE
    const contentToDownload = isCsv ? ('\uFEFF' + fileContent) : fileContent;
    const mimeType = isCsv ? 'text/csv;charset=utf-8;' : 'text/plain;charset=utf-8;';

    const blob = new Blob([contentToDownload], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const downloadSageCsvFile = (fileContent, filename) => {
    const baseName = filename.replace(/\.(txt|csv)$/i, '');
    downloadSageFile(fileContent, `${baseName}.csv`);
};

// ─── NUMÉROTATION SÉQUENTIELLE OFFICIELLE AFT ────────────────────────────────

export const generateNextSelectionNumber = () => {
    const ym = dayjs().format('YYYY-MM');
    const key = `aft_sage_num_sel_${ym}`;
    const nextSeq = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(nextSeq));
    return `SEL-${ym}-${String(nextSeq).padStart(3, '0')}`;
};

export const generateNextSimulationNumber = () => {
    const ym = dayjs().format('YYYY-MM');
    const key = `aft_sage_num_sim_${ym}`;
    const nextSeq = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(nextSeq));
    return `SIM-${ym}-${String(nextSeq).padStart(3, '0')}`;
};

export const generateNextTransferNumber = () => {
    const ym = dayjs().format('YYYY-MM');
    const key = `aft_sage_num_trf_${ym}`;
    const nextSeq = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(nextSeq));
    return `TRF-${ym}-${String(nextSeq).padStart(3, '0')}`;
};
